import BaseModal from 'flow/components/ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import { KeyboardShortcut } from 'flow/types/KeyboardShortcut';
import { TypedMap } from 'flow/types/TypedMap';
import BaseInput from '../ui-components/BaseInput';
import Button from '../ui-components/Button';
import { KeyboardEvent, useState } from 'react';
import {
    denormalizeShortcut,
    normalizeShortcut,
} from 'flow/utils/shortcut-utils';
import { updateKeyboardShortcuts } from 'flow/actions/keyboard-shortcuts-actions';
import { setSnackbar } from 'flow/actions/app-actions';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppDispatch } from 'flow/hooks/useRedux';
interface Props {
    shortcut?: TypedMap<KeyboardShortcut>;
}

const EditKeyboardShortcutModal = ({ shortcut }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();

    const [shortcutValue, setShortcutValue] = useState('');

    const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        event.preventDefault(); // Prevent default browser behavior

        // Capture the pressed key and modifiers
        const key = event.key.toLowerCase();
        const modifiers = [
            event.ctrlKey ? 'ctrl' : '',
            event.metaKey ? 'cmd' : '',
            event.altKey ? 'alt' : '',
            event.shiftKey ? 'shift' : '',
        ]
            .filter(Boolean) // Remove empty strings
            .join('+');

        // Combine modifiers and key
        const newShortcut = modifiers ? `${modifiers}+${key}` : key;

        // Update the state with the new shortcut
        setShortcutValue(newShortcut);
    };

    const handleSave = () => {
        const updatedShortcut = shortcut.set(
            'value',
            denormalizeShortcut(shortcutValue),
        );
        dispatch(updateKeyboardShortcuts(updatedShortcut.toJS()));
        dispatch(
            setSnackbar({
                message: i18next.t('settings.keyboard.shortcuts.saved'),
            }),
        );
    };

    const handleResetKeyboardShortcut = () => {
        const updatedShortcut = shortcut.set(
            'value',
            shortcut.get('defaultValue'),
        );
        dispatch(updateKeyboardShortcuts(updatedShortcut.toJS()));
        closeModal();
        dispatch(
            setSnackbar({
                message: i18next.t('settings.keyboard.shortcuts.reset.success'),
            }),
        );
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('settings.keyboard.shortcuts.edit')}
            onSubmit={handleSave}
        >
            <div>
                <div className="mb-4 flex items-center justify-between gap-2">
                    <div className="flex flex-1">
                        <BaseInput
                            disabled={true}
                            value={normalizeShortcut(
                                shortcut.get('defaultValue'),
                            ).toUpperCase()}
                        />
                    </div>
                    <Button
                        color="primary"
                        onClick={handleResetKeyboardShortcut}
                    >
                        {i18next.t('settings.keyboard.shortcuts.reset')}
                    </Button>
                </div>

                <BaseInput
                    label={i18next.t('settings.keyboard.shortcuts.new')}
                    autofocus={true}
                    value={shortcutValue.toUpperCase()}
                    onKeyDown={handleKeyPress} // Capture key presses
                    readOnly={true} // Prevent manual typing
                />
            </div>
        </BaseModal>
    );
};

export default EditKeyboardShortcutModal;
